import type { Building } from "building-types/building/buildingTypes";
import type { CaseworkPlanNote, CaseworkTag, CaseworkTagPlacement } from "building-types/casework/caseworkTypes";
import type { PolyLine2 } from "geom/polyLine2Types";

interface CaseworkPlanInfo {
  readonly roomId: string;
  readonly caseworkId: string;
  readonly solidLines: PolyLine2[];
  readonly dashLines: PolyLine2[];
  readonly pocheLines: PolyLine2[];
  readonly hiddenLines: PolyLine2[];
  readonly notes: CaseworkPlanNote[];
  readonly tag?: CaseworkTag;
  readonly tagPlacements?: CaseworkTagPlacement[];
}

export function extractLevelCaseworkPlanInfo(building: Building, levelId: string): CaseworkPlanInfo[] {
  const caseworkRoomLines: CaseworkPlanInfo[] = [];
  for (const roomId of building.roomsByLevel[levelId]) {
    extractRoomCaseworkPlanInfo(building, roomId, caseworkRoomLines);
  }

  return caseworkRoomLines;
}

function extractRoomCaseworkPlanInfo(building: Building, roomId: string, caseworkRoomLines: CaseworkPlanInfo[] = []) {
  for (const caseworkId in building.casework) {
    const casework = building.casework[caseworkId];

    if (casework.room.id !== roomId) {
      continue;
    }

    /* // uncomment to debug empty casework
    const hasEmptySolidLine = casework.planSolid.findIndex((lines) => lines.length === 0) !== -1;
    const hasEmptyDashedLine = casework.planDash.findIndex((lines) => lines.length === 0) !== -1;
    if (hasEmptyDashedLine || hasEmptySolidLine) {
      console.warn("casework with empty lines")
      console.warn(casework);
    }*/

    const solidLines: PolyLine2[] = [];
    const dashLines: PolyLine2[] = [];
    const pocheLines: PolyLine2[] = [];
    const hiddenLines: PolyLine2[] = [];

    solidLines.push(...casework.planSolid.filter((lines) => lines.length > 0));
    dashLines.push(...casework.planDash.filter((lines) => lines.length > 0));
    pocheLines.push(...casework.planPoche.filter((lines) => lines.length > 0));
    hiddenLines.push(...casework.planHidden.filter((lines) => lines.length > 0));
    dashLines.push(...casework.closetShelfRodLines.filter((lines) => lines.length > 0));

    const extrusions = [...casework.decorativeExtrusions, ...casework.wallSweepExtrusions];
    for (const ext of extrusions) {
      if (ext.dirX === 0 && ext.dirY === 0 && ext.dirZ === 1) {
        solidLines.push(ext.profile.loops[0]);
      }
    }

    caseworkRoomLines.push({
      caseworkId,
      roomId,
      solidLines,
      dashLines,
      pocheLines,
      hiddenLines,
      notes: casework.planNotes,
      tag: casework.tag,
      tagPlacements: casework.tagPlacements,
    });
  }
}
